import { useState } from 'react';
import { Search, Package, Truck, CheckCircle, Clock, XCircle, MapPin, RefreshCw } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const STATUS_STEPS = ['pending', 'confirmed', 'packed', 'shipped', 'out_for_delivery', 'delivered'];

const STEP_CONFIG = {
  pending:          { label: 'Order Placed',       icon: Clock,        color: 'text-yellow-500',  bg: 'bg-yellow-100' },
  confirmed:        { label: 'Order Confirmed',    icon: CheckCircle,  color: 'text-blue-500',    bg: 'bg-blue-100' },
  packed:           { label: 'Packed',             icon: Package,      color: 'text-purple-500',  bg: 'bg-purple-100' },
  shipped:          { label: 'Shipped',            icon: Truck,        color: 'text-indigo-500',  bg: 'bg-indigo-100' },
  out_for_delivery: { label: 'Out for Delivery',   icon: Truck,        color: 'text-orange-500',  bg: 'bg-orange-100' },
  delivered:        { label: 'Delivered',          icon: CheckCircle,  color: 'text-green-500',   bg: 'bg-green-100' },
  cancelled:        { label: 'Cancelled',          icon: XCircle,      color: 'text-red-500',     bg: 'bg-red-100' },
  refunded:         { label: 'Refunded',           icon: XCircle,      color: 'text-gray-500',    bg: 'bg-gray-100' },
};

function StatusTimeline({ tracking, currentStatus }) {
  if (currentStatus === 'cancelled' || currentStatus === 'refunded') {
    const cfg = STEP_CONFIG[currentStatus];
    const Icon = cfg.icon;
    return (
      <div className={`flex items-center gap-3 p-4 rounded-xl ${cfg.bg}`}>
        <Icon size={24} className={cfg.color} />
        <div>
          <p className={`font-semibold ${cfg.color}`}>{cfg.label}</p>
          <p className="text-xs text-gray-500 mt-0.5">{tracking?.[tracking.length - 1]?.message}</p>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_STEPS.indexOf(currentStatus);

  return (
    <div className="space-y-0">
      {STATUS_STEPS.map((status, i) => {
        const cfg = STEP_CONFIG[status];
        const Icon = cfg.icon;
        const done = i <= currentIdx;
        const active = i === currentIdx;
        const trackEntry = tracking?.find(t => t.status === status);
        const date = trackEntry ? new Date(trackEntry.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : null;

        return (
          <div key={status} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                done ? (active ? 'bg-brand-yellow' : 'bg-green-100') : 'bg-gray-100'
              }`}>
                <Icon size={16} className={done ? (active ? 'text-white' : 'text-green-600') : 'text-gray-300'} />
              </div>
              {i < STATUS_STEPS.length - 1 && (
                <div className={`w-0.5 h-8 mt-1 ${i < currentIdx ? 'bg-green-300' : 'bg-gray-100'}`} />
              )}
            </div>
            <div className="pb-8 pt-1.5 flex-1 min-w-0">
              <p className={`text-sm font-semibold ${done ? 'text-brand-dark' : 'text-gray-300'}`}>{cfg.label}</p>
              {date && <p className="text-xs text-gray-400 mt-0.5">{date}</p>}
              {trackEntry?.message && active && (
                <p className="text-xs text-brand-yellow font-medium mt-1">{trackEntry.message}</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Courier scan history. The events arrive with the order (the track endpoint syncs
// NimbusPost before responding), so there is nothing to click — Refresh just pulls
// again for a customer watching the page.
function LiveTrackingSection({ orderNumber, initialEvents }) {
  const [events, setEvents] = useState(initialEvents || []);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/shipping/live-track/${orderNumber}`);
      if (data.success) setEvents(Array.isArray(data.events) ? data.events : []);
      else toast.error(data.message || 'No live tracking data');
    } catch {
      toast.error('Failed to fetch live tracking');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-5 pt-4 border-t border-gray-50">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-700">Live Courier Updates</p>
        <button
          onClick={refresh}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-brand-yellow font-semibold hover:text-yellow-600 disabled:opacity-50"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {events.length === 0 ? (
        <p className="text-xs text-gray-400">No courier updates yet. Check back once the package is picked up.</p>
      ) : (
        <div className="space-y-2">
          {events.map((e, i) => (
            <div key={i} className="flex gap-3 text-sm bg-gray-50 rounded-xl px-3 py-2.5">
              <div className="w-2 h-2 rounded-full bg-brand-yellow mt-1.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-gray-800">{e.status || '—'}</p>
                {e.location && <p className="text-xs text-gray-400 mt-0.5">{e.location}</p>}
                {e.time && <p className="text-xs text-gray-400 mt-0.5">{e.time}</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrackOrderPage() {
  const [orderNo, setOrderNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);

  const handleTrack = async (e) => {
    e?.preventDefault();
    // Strip the leading "#" (display-only prefix) and spaces — order numbers are stored without it,
    // and a "#" in the URL path would be treated as a fragment and never reach the server.
    const cleaned = orderNo.trim().replace(/^#+/, '').replace(/\s+/g, '').toUpperCase();
    if (!cleaned) return;
    setLoading(true);
    setOrder(null);
    setLiveEvents([]);
    try {
      const { data } = await api.get(`/orders/track/${encodeURIComponent(cleaned)}`);
      if (typeof data.order.shipping_address === 'string') {
        try { data.order.shipping_address = JSON.parse(data.order.shipping_address); } catch {}
      }
      setOrder(data.order);
      setLiveEvents(data.live_events || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Order not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-8 md:py-14">
      <div className="text-center mb-8">
        <div className="w-14 h-14 bg-brand-yellow-light rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Truck size={28} className="text-brand-yellow" />
        </div>
        <h1 className="text-2xl font-bold text-brand-dark">Track Your Order</h1>
        <p className="text-gray-400 text-sm mt-1">Enter your order number to check delivery status</p>
      </div>

      <form onSubmit={handleTrack} className="flex gap-2 mb-8">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={orderNo}
            onChange={e => setOrderNo(e.target.value.toUpperCase())}
            placeholder="e.g. YLV-ABC123-WXYZ"
            className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-brand-yellow transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !orderNo.trim()}
          className="btn-primary px-5 py-3 rounded-xl font-semibold disabled:opacity-50 flex items-center gap-2"
        >
          {loading
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : 'Track'
          }
        </button>
      </form>

      {order && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          {/* Order info */}
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-50">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Order Number</p>
              <p className="font-bold text-brand-dark">{order.order_number}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400 mb-0.5">Total</p>
              <p className="font-bold text-brand-dark">₹{Number(order.total).toLocaleString('en-IN')}</p>
            </div>
          </div>

          {/* Shipping address */}
          {order.shipping_address && (
            <div className="flex gap-2 mb-5 pb-4 border-b border-gray-50">
              <MapPin size={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p className="font-medium text-brand-dark">{order.shipping_address.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {order.shipping_address.address_line1}, {order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}
                </p>
              </div>
            </div>
          )}

          {/* Courier partner + AWB */}
          {order.tracking_number && (
            <div className="mb-5 pb-4 border-b border-gray-50 flex flex-wrap gap-x-8 gap-y-3">
              {order.courier_name && (
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Courier Partner</p>
                  <p className="flex items-center gap-1.5 font-semibold text-brand-dark text-sm">
                    <Truck size={14} className="text-brand-yellow" />
                    {order.courier_name}
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Courier Tracking ID (AWB)</p>
                <p className="font-semibold text-brand-dark text-sm font-mono">{order.tracking_number}</p>
              </div>
            </div>
          )}

          {/* Internal status timeline */}
          <StatusTimeline tracking={order.tracking} currentStatus={order.status} />

          {/* Live courier updates (only if AWB exists) */}
          {order.tracking_number && (
            <LiveTrackingSection orderNumber={order.order_number} initialEvents={liveEvents} />
          )}
        </div>
      )}
    </div>
  );
}
